import { useCurrentUserProfile } from "@/hooks/useUsers";
import { useClientSwitcherStore } from "@/stores/client-switcher.store";

/**
 * The client this user's requests are actually scoped to — the client-side
 * mirror of `requireClientScope` (`apps/backend/src/plugins/auth.ts`).
 *
 * Use it for BOTH the `enabled` gate and the queryKey prefix of any client-scoped
 * query. Gating on the client switcher alone is what left `admin` and `member`
 * staring at an empty Pedidos list: the switcher is only ever written by
 * super-admin / account-manager surfaces, so for everyone else it stays null
 * forever and the query never fires. A disabled query in TanStack Query v5 also
 * reports `isLoading === false`, so the page skips its loading branch and renders
 * the empty state — indistinguishable from a tenant with no data.
 *
 * Three branches, three different reasons:
 *  - Switcher roles (super_admin, account_manager) own no client, so their scope
 *    can only come from the switcher — `api.ts` sends it as `X-Client-Id`. Null
 *    while nothing is selected keeps the query off, which is what avoids the 403
 *    the original gate existed to avoid.
 *  - `admin` / `member` never send that header and the backend ignores it for
 *    them anyway: their scope is their own profile, so their query must fire
 *    without any switcher at all.
 *  - As a queryKey prefix, the id is what stops a super-admin from reading the
 *    previous school's rows out of cache after switching.
 */
export function useScopedClientId(): string | null {
  const profile = useCurrentUserProfile();
  const activeClientId = useClientSwitcherStore((s) => s.activeClientId);

  // Profile still loading: no scope is known yet, so callers must stay disabled
  // rather than fire a request the backend would reject.
  if (!profile) return null;

  const usesClientSwitcher =
    profile.role === "super_admin" || profile.role === "account_manager";
  return usesClientSwitcher ? activeClientId : profile.clientId;
}
